import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Tooltip,
	CircularProgress,
	IconButton,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArchiveIcon from '@mui/icons-material/Archive';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import { Delete } from '@mui/icons-material';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useObjectContext } from '@/hooks/useObjectContext';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useDeleteDealMutation, useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEAL_STATUS_TYPES, getObjectType } from '@/utils/constants';
import { paths } from '@/app/routes';
import { useDispatch } from 'react-redux';
import { hideToast, showToast } from '@/features/toast/toastSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

export const DealMenu = ({ minimal }) => {
	const { dealRow, clientRow, dealId } = useObjectContext();

	const isArchived = dealRow?.status === DEAL_STATUS_TYPES.archived;
	const objectType = dealRow?.objectType;

	useEffect(() => console.log('Deal id', dealId), [dealId]);

	const isClaim = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const isNewBusiness = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isRenewal = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_RENEWALS;

	const isDebtor = dealRow?.pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;

	const objectLabel = getObjectType(dealRow?.objectType);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const anchorRef = useRef(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const [dialogConfig, setDialogConfig] = useState({
		open: false,
		action: '',
		title: '',
		message: '',
		confirmAction: () => {},
		canBeUndone: true,
	});

	const [deleteDeal, { isLoading: isDeleting }] = useDeleteDealMutation({
		fixedCacheKey: `delete-deal-${dealId}-${objectType}`,
	});
	const [updateDeal, { isLoading }] = useUpdateDealRowMutation();

	const { getFeatureFlags } = useFeatureFlagContext();
	const showSOA = Boolean(getFeatureFlags('statementOfAdvice')?.active);
	const showEmail = Boolean(getFeatureFlags('emails')?.active);
	const showUnlayerEmails = Boolean(getFeatureFlags('unlayerEmails')?.active);

	const soaDisabled = useMemo(() => {
		const needs = (dealRow?.needsAnalysis ?? []).map((r) => r?.key);
		const risks = dealRow?.riskClasses ?? [];
		return needs.length === 0 || risks.some((r) => !needs.includes(r));
	}, [dealRow?.needsAnalysis, dealRow?.riskClasses]);

	const handleClick = (event) => setAnchorEl(event.currentTarget);
	const handleToggle = () => {
		setAnchorEl(anchorRef.current);
	};
	const handleClose = () => setAnchorEl(null);

	const handleDialogOpen = (config) => {
		setDialogConfig({
			open: true,
			canBeUndone: true,
			...config,
		});
		handleClose();
	};

	const handleDialogClose = () => {
		setDialogConfig((prev) => ({ ...prev, open: false }));
	};

	const archiveDeal = async () => {
		await updateDeal({
			dealId,
			docId: dealRow?.id,
			properties: {
				status: DEAL_STATUS_TYPES.archived,
				// isRenewable: false,
			},
		}).unwrap();
		handleDialogClose();
	};

	const unarchiveDeal = async () => {
		await updateDeal({
			dealId,
			docId: dealRow?.id,
			properties: {
				status: null,
				// isRenewable: true,
			},
		}).unwrap();
		handleDialogClose();
	};

	const handleDelete = async () => {
		// const path = getUrlFromPipeline(dealRow?.pipeline);
		const clientId = getDealClientId(dealRow);
		const toastId = Math.random();

		dispatch(
			showToast({
				message: `Deleting deal ${dealId} '${dealRow?.dealName}'...`,
				autohide: false,
				loading: true,
				id: toastId,
			}),
		);

		if (clientId) {
			navigate(`${paths.clients}/${clientId}`);
		}
		// navigate(path);

		try {
			console.log('dealId: ', dealId, objectType);

			await deleteDeal({ dealId, objectType }).unwrap();

			// if (driveItem) {
			// 	await renameSharepointItem({
			// 		driveId: driveItem?.parentReference?.driveId,
			// 		itemId: driveItem?.id,
			// 		name: `[DELETED] - ${driveItem?.name}`,
			// 	}).unwrap();
			// }

			dispatch(hideToast(toastId));
			dispatch(
				showToast({
					message: `Deal ${dealId} successfully deleted.`,
				}),
			);
		} catch (err) {
			console.error(`Error deleting deal ${dealId}`, err);
			dispatch(
				showToast({
					message: 'Error deleting deal; please try again.',
					error: true,
				}),
			);
		}
	};

	const archiveConfig = {
		action: 'archive',
		title: `Archive ${objectLabel}`,
		message: `Archiving a ${objectLabel} removes it from your active ${objectLabel}s list.`,
		confirmAction: archiveDeal,
		icon: <ArchiveIcon />,
		buttonText: `Archive ${objectLabel}`,
	};

	const unarchiveConfig = {
		action: 'unarchive',
		title: `Unarchive ${objectLabel}`,
		confirmAction: unarchiveDeal,
		icon: <UnarchiveIcon />,
		buttonText: `Unarchive ${objectLabel}`,
	};

	const deleteConfig = {
		action: 'delete',
		title: `Delete ${objectLabel}`,
		message: `This action cannot be undone. The ${objectLabel} and its associated files will be permanently removed.`,
		confirmAction: handleDelete,
		canBeUndone: false,
		icon: <Delete color='error' />,
		buttonText: `Delete ${objectLabel}`,
	};

	const locationPaths = location.pathname.split('/').filter(Boolean);

	const renderMenuItems = () => (
		<>
			{(showEmail || showUnlayerEmails) && (
				<>
					{showEmail && !showUnlayerEmails && (
						<MenuItem
							onClick={() => {
								handleClose();
								dispatch(openDialog('email'));
							}}
						>
							<ListItemIcon>
								<EmailIcon />
							</ListItemIcon>
							<ListItemText>Send Email</ListItemText>
						</MenuItem>
					)}
					{showUnlayerEmails && (
						<MenuItem
							onClick={() => {
								handleClose();
								dispatch(
									openDialog({
										dialogName: 'unlayerEmail',
										data: {
											clientLocked: true,
											dealLocked: true,
											client: clientRow ?? null,
											deal: dealRow ?? null,
										},
									}),
								);
							}}
						>
							<ListItemIcon>
								<EmailIcon />
							</ListItemIcon>
							<ListItemText>Send Email</ListItemText>
						</MenuItem>
					)}
				</>
			)}

			{/* {showSOA && (isNewBusiness || isRenewal) && (
				<Tooltip
					title={soaDisabled ? 'Please update needs analysis in Deal Details tab' : ''}
				>
					<div>
						<MenuItem
							disabled={soaDisabled}
							onClick={() =>
								navigate(`/${locationPaths[0]}/${dealId}${paths.generateSOA}`)
							}
						>
							<ListItemIcon>
								<EditIcon />
							</ListItemIcon>
							<ListItemText>Generate Statement of Advice</ListItemText>
						</MenuItem>
					</div>
				</Tooltip>
			)} */}
			<Tooltip title={isArchived ? unarchiveConfig.message : archiveConfig.message}>
				<MenuItem
					disabled={isLoading}
					onClick={() => handleDialogOpen(isArchived ? unarchiveConfig : archiveConfig)}
				>
					<ListItemIcon>{isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}</ListItemIcon>
					<ListItemText sx={{ fontWeight: 'bold' }}>
						{isArchived ? `Unarchive ${objectLabel}` : `Archive ${objectLabel}`}
					</ListItemText>
				</MenuItem>
			</Tooltip>
			<div>
				<MenuItem disabled={isDeleting} onClick={() => handleDialogOpen(deleteConfig)}>
					<ListItemIcon sx={{ color: 'error.main' }}>
						{isDeleting ? <CircularProgress size='1em' /> : <Delete />}
					</ListItemIcon>
					<ListItemText sx={{ color: 'error.main', fontWeight: 'bold' }}>
						{`Delete ${objectLabel}`}
					</ListItemText>
				</MenuItem>
			</div>
		</>
	);

	return (
		<>
			{minimal ? (
				<IconButton aria-label='more' ref={anchorRef} onClick={handleToggle}>
					<MoreHorizIcon />
				</IconButton>
			) : (
				<Button
					id='deal-menu-button'
					variant='tab_header'
					aria-controls={open ? 'deal-menu' : undefined}
					aria-haspopup='true'
					aria-expanded={open ? 'true' : undefined}
					onClick={handleClick}
					endIcon={<ArrowDropDownIcon />}
				>
					Actions
				</Button>
			)}

			<Menu
				id='deal-menu'
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				MenuListProps={{ 'aria-labelledby': 'deal-menu-button' }}
			>
				{renderMenuItems()}
			</Menu>
			<ConfirmDialog
				openDialog={dialogConfig.open}
				handleClose={handleDialogClose}
				handleConfirm={dialogConfig.confirmAction}
				actionName={dialogConfig.action}
				title={dialogConfig.title}
				canBeUndone={dialogConfig.canBeUndone}
				additionalContent={dialogConfig.message}
			/>
		</>
	);
};
